<template>
  <div class="form-group" :class="classList">
    <label v-if="$slots.label || props.label" :class="labelClassList" :for="key">
      <template v-if="$slots.label">
        <slot name="label"/>
      </template>
      <template v-else>
        {{ props.label }}
        <span v-if="props.required" class="form-group-asterisk">*</span>
      </template>
    </label>

    <div :class="isHorizontal ? horizontalInputClassList : verticalInputClassList">
      <slot/>
      <template v-if="$slots.hint">
        <span class="form-text"><slot name="hint"/></span>
      </template>
      <template v-if="props.hint">
        <span class="form-text text-muted">{{ props.hint }}</span>
      </template>
      <template v-if="props.errorName || errorsSource">
        <s-error :error-name="props.errorName" :errors="errorsSource"/>
      </template>
    </div>
  </div>
</template>

<script setup>
  import Ids from '@/utils/ids';
  import { computed, inject, getCurrentInstance } from 'vue';
  import { useFieldErrors } from '@/composables/field-errors';

  const ID = new Ids(`form-group-${getCurrentInstance().uid}`);

  const props = defineProps({
    label: {
      type: String,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    for: {
      type: String,
      default: null,
    },
    horizontal: {
      type: Boolean,
      default: null,
    },
    contentClass: {
      type: String,
      default: null,
    },
    hint: {
      type: String,
      default: null,
    },
    errorName: {
      type: [String, Array],
      default: null,
    },
  });

  const formOptions = inject('formOptions', null);

  const key = props.for || ID.nextPositive();
  const { errorsSource, hasError } = useFieldErrors(formOptions?.formOptions?.fieldErrors, props.errorName);

  const isHorizontal = computed(() => {
    if (typeof props.horizontal === 'boolean') {
      return props.horizontal;
    }

    if (formOptions?.formOptions.fieldLayoutHorizontal) {
      return formOptions.formOptions.fieldLayoutHorizontal;
    }

    return false;
  });

  const labelClassList = computed(() => {
    const list = ['form-group__label'];

    if (isHorizontal.value) {
      list.push('col-form-label');
    }

    return list;
  });

  const horizontalInputClassList = computed(() => ['w-100'].concat(props.contentClass));

  const verticalInputClassList = computed(() => props.contentClass);

  const classList = computed(() => {
    const list = [];

    if (isHorizontal.value) {
      list.push('form-group--horizontal');
    }

    if (hasError.value) {
      list.push('form-group--has-error');
    }

    return list;
  });
</script>
